import React, { useState, useEffect } from 'react';
import { Step } from 'react-joyride';
import OnboardingTour from './OnboardingTour';
import { useOnboarding } from '../../hooks/useOnboarding';
import { useLanguage } from '../../contexts/LanguageContext';

interface FlowBuilderTourProps {
  forceShow?: boolean;
}

const FlowBuilderTour: React.FC<FlowBuilderTourProps> = ({ forceShow = false }) => {
  const { showTour: autoShowTour, completeTour, skipTour } = useOnboarding('flow-builder');
  const [showTour, setShowTour] = useState(false);
  const { t } = useLanguage();

  // Auto-show only if user hasn't completed/skipped the tour, OR if manually triggered
  useEffect(() => {
    if (autoShowTour || forceShow) {
      setShowTour(true);
    }
  }, [autoShowTour, forceShow]);

  const steps: Step[] = [
    {
      target: 'body',
      content: (
        <div>
          <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {t('onboarding.flowBuilder.steps.welcome.title')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 text-base leading-relaxed">
            {t('onboarding.flowBuilder.steps.welcome.content')}
          </p>
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              💡 <strong>Tipp:</strong> Használd a nyilakat a navigáláshoz, vagy kattints bárhova a kihagyáshoz.
            </p>
          </div>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '.react-flow',
      content: (
        <div>
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            🖼️ {t('onboarding.flowBuilder.steps.canvas.title')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-3">
            {t('onboarding.flowBuilder.steps.canvas.content')}
          </p>
          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <li className="flex items-start gap-2">
              <span className="text-green-500">✓</span>
              <span>Húzd át a node-okat a sidebar-ból</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500">✓</span>
              <span>Kösd össze őket vonalakkal</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500">✓</span>
              <span>Zoom: egér görgő vagy touchpad</span>
            </li>
          </ul>
        </div>
      ),
      placement: 'center',
    },
    {
      target: '.node-sidebar',
      content: (
        <div>
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            🧩 {t('onboarding.flowBuilder.steps.sidebar.title')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-3">
            {t('onboarding.flowBuilder.steps.sidebar.content')}
          </p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded">
              <span className="font-semibold">⚡ Trigger</span>
            </div>
            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
              <span className="font-semibold">🎯 Action</span>
            </div>
            <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded">
              <span className="font-semibold">🔀 Logic</span>
            </div>
            <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded">
              <span className="font-semibold">📊 Data</span>
            </div>
          </div>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '.properties-panel',
      content: (
        <div>
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            ⚙️ {t('onboarding.flowBuilder.steps.properties.title')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-3">
            {t('onboarding.flowBuilder.steps.properties.content')}
          </p>
          <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg">
            <p className="text-sm text-purple-700 dark:text-purple-300">
              <strong>Pro tipp:</strong> Kattints egy node-ra a canvas-on a részletes beállításokhoz!
            </p>
          </div>
        </div>
      ),
      placement: 'left',
    },
    {
      target: '.flow-toolbar',
      content: (
        <div>
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            🛠️ {t('onboarding.flowBuilder.steps.toolbar.title')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-3">
            {t('onboarding.flowBuilder.steps.toolbar.content')}
          </p>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-2xl">💾</span>
              <span className="text-gray-600 dark:text-gray-400">Mentés</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">⚡</span>
              <span className="text-gray-600 dark:text-gray-400">Futtatás</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">📋</span>
              <span className="text-gray-600 dark:text-gray-400">Műveletek</span>
            </div>
          </div>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: '.terminal-button',
      content: (
        <div>
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            📟 Execution Terminál
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-3">
            Valós idejű végrehajtási logok és eredmények. Kattints ide a terminál megnyitásához/bezárásához.
          </p>
          <div className="p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800">
            <p className="text-sm text-cyan-700 dark:text-cyan-300">
              <strong>💡 Tipp:</strong> A terminál automatikusan megnyílik flow futtatáskor és megjeleníti a részletes logokat.
            </p>
          </div>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: '.flow-manager-button',
      content: (
        <div>
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            🎛️ Flow Manager
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-3">
            Ütemezett flow-k kezelése. Itt láthatod az összes időzített futtatást és módosíthatod őket.
          </p>
          <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span>Aktív ütemezések listája</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span>Cron kifejezések szerkesztése</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span>Ütemezés ki/be kapcsolása</span>
            </div>
          </div>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: '.actions-menu-button',
      content: (
        <div>
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            📋 Műveletek Menü
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-3">
            Minden fontos funkció egy helyen: új flow, sablonok, import/export, API kulcsok és még sok más!
          </p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
              <span className="font-semibold">➕ Új Flow</span>
            </div>
            <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded">
              <span className="font-semibold">📑 Sablonok</span>
            </div>
            <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded">
              <span className="font-semibold">📥 Import</span>
            </div>
            <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded">
              <span className="font-semibold">🔑 API Kulcsok</span>
            </div>
          </div>
          <div className="mt-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <strong>🎯 Itt találod:</strong> Visual Tour - bármikor újraindíthatod a körbevezetést!
            </p>
          </div>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: 'body',
      content: (
        <div>
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            🎉 {t('onboarding.progress.completed')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Most már készen állsz az első flow létrehozására! Kezdd egy egyszerű példával, majd fokozatosan bővítsd.
          </p>
          <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <p className="text-sm text-green-700 dark:text-green-300 mb-2">
              <strong>💡 Következő lépések:</strong>
            </p>
            <ul className="space-y-1 text-sm text-green-600 dark:text-green-400">
              <li>• Próbáld ki a sablonokat</li>
              <li>• Nézd meg a dokumentációt</li>
              <li>• Indítsd újra a tour-t bármikor: Műveletek → Visual Tour</li>
            </ul>
          </div>
        </div>
      ),
      placement: 'center',
    },
  ];

  const handleComplete = () => {
    setShowTour(false);
    completeTour();
  };

  const handleSkip = () => {
    setShowTour(false);
    skipTour();
  };

  return (
    <OnboardingTour
      steps={steps}
      run={showTour}
      onComplete={handleComplete}
      onSkip={handleSkip}
    />
  );
};

export default FlowBuilderTour;
